import { useState, useCallback } from 'react';
import { useHistory } from 'react-router';
import { t } from 'ttag';
import _ from 'lodash';
import {
  Invite as sdkInvite,
  User as sdkUser,
  xhr as sdkXhr,
  Services as sdkServices
} from 'coolremote-sdk';
import { useStoreActions, useStoreState } from '../../../models/RootStore';

export const useInviteActions = (inviteInfo, inviteToken) => {
  const history = useHistory();

  // Store state and actions
  const userPref = useStoreState((s) => s.users.userPreferences);
  const updateUserPreferences = useStoreActions((a) => a.users.updateUserPreferences);
  const getUserPreferences = useStoreActions((a) => a.users.getUserPreferences);
  const addMessage = useStoreActions((action) => action.errorMessage.addMessage);
  const doLogin = useStoreActions((a) => a.doLogin);

  // Local state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [loginState, setLoginState] = useState({});
  const [subCode, setSubCode] = useState('');

  const goToHome = useCallback(() => {
    window.location.href = "/";
  }, []);

  const acceptInvite = useCallback(async (token, subCode) => {
    try {
      let newUserPref = userPref || {};
      if (_.isEmpty(newUserPref)) {
        newUserPref = await getUserPreferences();
      }
      if (inviteInfo?.customerNickname && !newUserPref?.nicknames?.[inviteInfo.customer]) {
        const newNicknames = newUserPref?.nicknames || {};
        newNicknames[inviteInfo.customer] = inviteInfo.customerNickname;
        updateUserPreferences({ ...newUserPref, nicknames: newNicknames });
      }
    } catch (error) {
      // Ignore preference errors
    }

    setIsSubmitting(true);

    try {
      await sdkInvite.acceptInvite(token, subCode);
      history.push("/");
      window.location.reload();
    } catch (error) {
      setLoginState({ ...loginState, error: error.message });
      setFormError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [inviteInfo, userPref, getUserPreferences, updateUserPreferences, history, loginState]);

  const handleLogin = useCallback(async (values) => {
    if (inviteInfo?.setSubscription) {
      try {
        await sdkServices.verifySubCode(inviteToken, values.subscriptionCode);
        setSubCode(values.subscriptionCode);
        const res = await doLogin({
          username: values.username,
          password: values.password
        });
        return res;
      } catch (response) {
        if (response) {
          setLoginState({ ...loginState, error: response.message });
        }
        throw response;
      }
    }

    try {
      const res = await doLogin({
        username: values.username,
        password: values.password
      });
      return res;
    } catch (response) {
      if (response) {
        setLoginState({ ...loginState, error: response.message });
      }
      throw response;
    }
  }, [inviteInfo, inviteToken, doLogin, loginState]);

  const handleInviteAcceptance = useCallback(async (formData) => {
    if (!formData.firstName || !formData.lastName || !formData.password || !formData.confirmPassword) {
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const { phone } = formData;
      const newPhone = phone.replace(/[+]/g, "");

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        subscriptionCode: formData.subscriptionCode,
        invite: inviteToken,
        username: formData.email,
        appId: "CoolCommercial",
        phone: newPhone,
        timeFormat: formData.timeFormat,
        dateFormat: formData.dateFormat,
        temperatureScale: formData.temperatureScale
      };

      const resp = await sdkUser.createUser(userData);

      if (!resp) {
        throw new Error('Failed to create user');
      }

      localStorage.setItem("token", resp.token);
      await sdkXhr.setToken(resp.token);

      if (inviteInfo?.customerNickname) {
        const userPref = {
          nicknames: {
            [inviteInfo.customer]: inviteInfo.customerNickname
          }
        };
        await updateUserPreferences(userPref);
      }

      goToHome();
    } catch (error) {
      addMessage({ message: error.message, onclick: goToHome });
    } finally {
      setIsSubmitting(false);
    }
  }, [inviteToken, inviteInfo, updateUserPreferences, addMessage, goToHome]);

  return {
    isSubmitting,
    formError,
    loginState,
    subCode,
    setSubCode,
    acceptInvite,
    handleLogin,
    handleInviteAcceptance,
    setFormError,
    setLoginState
  };
};
